const specialFeaturesURL = process.env.VUE_APP_URL;

// Headers are built PER CALL — the IntegrationModule-style module-load capture
// goes stale after a re-login without a full page reload.
const authHeaders = (json = false) => {
    const headers = {
        'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('access_token')),
    };
    if (json) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};

/**
 * Special Features registry — CRUD over the admin-server's /specialfeatures
 * module (direct Prisma, no ?service relay) plus the template link writer.
 *
 * The link used to be a column on ManholeTemplate and so worked for exactly
 * one of the element types the registry can hold a feature for. It is now a
 * row naming its own element type, and a template may carry several — see
 * linkSpecialFeature below.
 */
const specialFeaturesModule = {
    state: () => ({
        specialFeatures: [],
        specialFeatureTypes: [
            { label: 'Detailed Survey', value: 'DETAILED_SURVEY' },
        ],
    }),
    mutations: {
        setSpecialFeatures(state, specialFeatures) {
            state.specialFeatures = specialFeatures;
        },
    },
    actions: {
        async fetchSpecialFeatures(state, payload) {
            const query = payload && payload.networkElementId
                ? '?networkElementId=' + payload.networkElementId
                : '';
            const res = await fetch(
                specialFeaturesURL + '/specialfeatures' + query,
                { method: 'GET', headers: authHeaders() }
            );
            const data = await res.json();
            if (data.status == true) {
                state.commit('setSpecialFeatures', data.data || []);
            }
            return data;
        },
        async createSpecialFeature(state, payload) {
            const res = await fetch(
                specialFeaturesURL + '/specialfeatures',
                {
                    method: 'POST',
                    headers: authHeaders(true),
                    body: JSON.stringify(payload),
                }
            );
            const data = await res.json();
            if (data.status == true) {
                await state.dispatch('fetchSpecialFeatures');
            }
            return data;
        },
        async updateSpecialFeature(state, payload) {
            const res = await fetch(
                specialFeaturesURL + '/specialfeatures/' + payload.id,
                {
                    method: 'PUT',
                    headers: authHeaders(true),
                    body: JSON.stringify(payload),
                }
            );
            const data = await res.json();
            if (data.status == true) {
                await state.dispatch('fetchSpecialFeatures');
            }
            return data;
        },
        /**
         * Patch part of ONE feature's config, merged on the hub.
         *
         * payload: { id, templateId?, pairs?, ficheFields? }
         *   templateId + pairs  REPLACE that element template's T1.4 pairs
         *                       (`elementAttributeMap[templateId]`); [] clears them.
         *   ficheFields         merged key by key; a key sent as null is removed.
         *
         * PATCH /specialfeatures/:id/config (convention §14). The hub reads the
         * row FOR UPDATE and writes only these keys, so the pairing table and
         * the feature editor cannot overwrite each other.
         *
         * A thrown fetch becomes a refusal envelope, as in linkSpecialFeature,
         * so the caller's failure toast always runs. A Nest validation refusal
         * answers `message` as an array; it is joined here.
         */
        async mergeSpecialFeatureConfig(state, payload) {
            const id = payload && payload.id;
            if (!id) {
                return { status: false, message: 'No special feature selected' };
            }
            const body = {};
            if (payload.templateId !== undefined) body.templateId = payload.templateId;
            if (payload.pairs !== undefined) body.pairs = payload.pairs;
            if (payload.ficheFields !== undefined) body.ficheFields = payload.ficheFields;
            try {
                const res = await fetch(
                    specialFeaturesURL + '/specialfeatures/' + encodeURIComponent(id) + '/config',
                    {
                        method: 'PATCH',
                        headers: authHeaders(true),
                        body: JSON.stringify(body),
                    }
                );
                const data = await res.json();
                if (data && Array.isArray(data.message)) {
                    data.message = data.message.join('; ');
                }
                if (data && data.status == true) {
                    await state.dispatch('fetchSpecialFeatures');
                }
                return data;
            } catch (error) {
                console.error('Special feature config merge failed:', error);
                return { status: false, message: 'Could not reach the server to save the special feature settings' };
            }
        },
        async deleteSpecialFeature(state, payload) {
            const res = await fetch(
                specialFeaturesURL + '/specialfeatures/' + payload.id,
                { method: 'DELETE', headers: authHeaders() }
            );
            const data = await res.json();
            if (data.status == true) {
                await state.dispatch('fetchSpecialFeatures');
            }
            return data;
        },
        /**
         * Read the special features linked to ONE element template.
         *
         * payload: { elementType, templateId }
         *
         * GET /specialfeatures/links, which has existed on the hub since the
         * multi-select round and which NOTHING called until now. That is the
         * whole of the bug it fixes: the picker prefilled from the template
         * payload, no endpoint serves `specialFeatureTemplateIds` on a template
         * row, so the prefill was `[]` on every non-Manhole type — and the link
         * save has SET semantics, so the second save deleted the first one's
         * links.
         *
         * Deliberately NOT committed to module state. The answer belongs to one
         * template, and a module-level cache would show the previously loaded
         * template's links against the next one — the same class of stale-read
         * bug this is fixing, one layer up. The caller holds it beside the
         * template it loaded.
         *
         * Cost: one GET per template opened in Manage Element (admin console
         * only, never the field apps). On the hub it is the template row, a
         * findMany on specialFeatureTemplateLink (indexed by elementType +
         * templateId), the feature rows, and on Manhole one extra findUnique
         * for the legacy column.
         *
         * A blank argument is refused here instead of being sent: the hub
         * answers `status: false` for it anyway, and the caller reads a refusal
         * as "links unknown" — which is the safe state — so there is no reason
         * to spend the round trip.
         *
         * A thrown fetch becomes a refusal envelope, as in linkSpecialFeature:
         * a rejected promise would skip the caller's "links unknown" state and
         * leave the picker looking like a template with nothing linked.
         */
        async fetchSpecialFeatureLinks(state, payload) {
            const elementType = payload && typeof payload.elementType === 'string' ? payload.elementType.trim() : '';
            const templateId = payload && payload.templateId != null ? String(payload.templateId).trim() : '';
            if (elementType === '' || templateId === '') {
                return { status: false, message: 'Load a template first, then configure its special features' };
            }
            try {
                const query = '?elementType=' + encodeURIComponent(elementType)
                    + '&templateId=' + encodeURIComponent(templateId);
                const res = await fetch(
                    specialFeaturesURL + '/specialfeatures/links' + query,
                    { method: 'GET', headers: authHeaders() }
                );
                return await res.json();
            } catch (error) {
                console.error('Special feature links read failed:', error);
                return { status: false, message: 'Could not reach the server to read this template’s special features' };
            }
        },
        /**
         * Set the special features linked to ONE element template.
         *
         * payload: { elementType, templateId, specialFeatureTemplateIds }
         *   elementType   NetworkElement.type, e.g. 'FDT-SRO' — the link row
         *                 names its own type, so the template id alone no
         *                 longer says which table it lives in.
         *   templateId    the element template ROW id.
         *   specialFeatureTemplateIds  the FULL set the template should end up
         *                 with. An empty array clears every link.
         *
         * The body is built by linkPayloadForTemplate (see
         * components/integration/networkElements/specialFeatureLink.js) — the
         * two pages that call this name their ids the opposite way round.
         *
         * A thrown fetch is turned into a refusal envelope rather than an
         * unhandled rejection: the callers read the answer through
         * describeLinkOutcome, and a promise that rejects would skip the
         * failure toast entirely and leave the screen showing the selection as
         * though it had been saved.
         */
        async linkSpecialFeature(state, payload) {
            try {
                const res = await fetch(
                    specialFeaturesURL + '/specialfeatures/link',
                    {
                        method: 'POST',
                        headers: authHeaders(true),
                        body: JSON.stringify(payload),
                    }
                );
                return await res.json();
            } catch (error) {
                console.error('Special feature link request failed:', error);
                return { status: false, message: 'Could not reach the server to save the special feature links' };
            }
        },
    },
    getters: {
        getSpecialFeatures: state => state.specialFeatures,
        getSpecialFeatureTypes: state => state.specialFeatureTypes,
        getActiveSpecialFeatures: state =>
            state.specialFeatures.filter(feature => feature.status === 'active'),
    },
};

export default specialFeaturesModule;
